import axios from 'axios'
import store from '@/store'
import { MessageBox } from 'element-ui'
import { removeToken } from '@/utils/token'
import router from '@/router'

const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 5000
})

const authExpiredCodes = new Set([1011, 1008, 1007, 1006, 1002])
let reloginTask = null

function redirectToLogin() {
  router.replace('/login').catch(() => {})
}

function clearAuthState() {
  removeToken()
  store.commit('user/setToken', '')
  store.commit('user/setUser', '')
  store.commit("menu/setMenus", [])
  store.commit("menu/setRoutes", [])
  store.commit("menu/setDisplayMenus", [])
  store.commit("menu/setBtnMenus", [])
}

function triggerRelogin(message = '您的登录信息已过期，请重新登录') {
  clearAuthState()
  if (reloginTask) {
    return reloginTask
  }
  reloginTask = MessageBox.alert(message, '提示', {
    confirmButtonText: '确定',
    type: 'warning'
  }).catch(() => {})
    .then(() => {
      redirectToLogin()
    })
    .finally(() => {
      setTimeout(() => {
        reloginTask = null
      }, 300)
    })
  return reloginTask
}

// 重试配置
const retryConfig = {
  maxRetries: 2, // 最多重试2次
  retryDelay: 1000, // 重试延迟时间（毫秒）
  retryableStatusCodes: [408, 429, 500, 502, 503, 504], // 可重试的HTTP状态码
}

// 请求拦截器
instance.interceptors.request.use(
  function (config) {
    // 初始化重试计数
    config.__retryCount = config.__retryCount || 0

    if (config.method === 'post') {
      config.data = {
        ...config.data,
        _t: Date.parse(new Date()) / 1000
      }
    } else if (config.method === 'get') {
      config.params = {
        random: Math.random(),
        ...config.params
      }
    }
    const requestUrl = (config.url || '').split('?')[0]
    const requestMethod = (config.method || '').toLowerCase()
    const isLoginRequest = requestMethod === 'post' && (requestUrl === '/login' || requestUrl === 'login')
    config.__isLoginRequest = isLoginRequest
    const token = window.localStorage.getItem('token')
    if (!isLoginRequest && token) {
      config.headers.x_access_token = token
    }
    return config
  },
  function (err) {
    return Promise.reject(err)
  }
)

// 响应拦截器 - 参考若依框架处理方式
instance.interceptors.response.use(
  function (response) {
    const res = response.data

    // token过期或无效的错误码（1002/1006/1007/1011）以及用户不存在（1008）
    if (authExpiredCodes.has(res.code)) {
      triggerRelogin('您的登录信息已过期，请重新登录')
      // 返回一个特殊标记，避免在组件中产生未捕获的 Promise rejection
      // 组件中的 .then() 会收到这个响应但 code 不是 1000，会跳过数据处理
      return Promise.resolve(res)
    } else if (res.code === 1009) {
      // 账号被锁定
      removeToken()
      store.commit('user/setToken', '')
      store.commit('user/setUser', '')

      MessageBox.alert('您的账号已被锁定，请联系管理员', '错误', {
        confirmButtonText: '确定',
        type: 'error',
        callback: () => {
          redirectToLogin()
        }
      })
      // 返回响应，避免在组件中产生未捕获的 Promise rejection
      return Promise.resolve(res)
    } else if (response.status !== 200) {
      // HTTP 状态码错误
      MessageBox.alert('系统出现错误，请联系管理员', '错误', {
        confirmButtonText: '确定',
        type: 'error'
      })
      return Promise.reject(new Error(res.message || '系统错误'))
    }
    return res
  },
  function (err) {
    // 处理网络错误或请求失败
    const config = err.config

    // 判断是否应该重试
    const shouldRetry = () => {
      if (!config || config.__retryCount >= retryConfig.maxRetries) {
        return false
      }
      // 超时错误总是可以重试
      if (err.code === 'ECONNABORTED') {
        return true
      }
      // 网络错误可以重试（未建立连接）
      if (err.message === 'Network Error' || !err.response) {
        return config.method === 'get' // 仅GET请求重试
      }
      // 特定HTTP状态码可以重试
      if (err.response && retryConfig.retryableStatusCodes.includes(err.response.status)) {
        return config.method === 'get' // 仅GET请求重试
      }
      return false
    }

    // 执行重试
    if (shouldRetry()) {
      config.__retryCount++
      const delayMs = retryConfig.retryDelay * Math.pow(2, config.__retryCount - 1) // 指数退避

      console.warn(
        `Request failed: ${config.url}, retrying... (${config.__retryCount}/${retryConfig.maxRetries}) after ${delayMs}ms`,
        err.message
      )

      return new Promise(resolve => {
        setTimeout(() => {
          resolve(instance.request(config))
        }, delayMs)
      })
    }

    let message = '系统出现错误，请联系管理员'
    const isTimeoutError = err.code === 'ECONNABORTED'

    if (err.response) {
      // 服务器响应了错误状态码
      const status = err.response.status
      if (status === 401) {
        // 未授权 - token无效或过期
        message = '您的登录信息已过期，请重新登录'
        if (!config || !config.__isLoginRequest) {
          triggerRelogin(message)
        }
      } else if (status === 403) {
        message = '您没有权限访问此资源'
      } else if (status === 404) {
        message = '请求的资源不存在'
      } else if (status === 500) {
        message = '服务器内部错误'
      }

      // 非401错误显示提示
      if (status !== 401) {
        MessageBox.alert(message, '错误', {
          confirmButtonText: '确定',
          type: 'error'
        })
      }
    } else if (isTimeoutError) {
      // 请求超时
      message = '请求超时，服务器响应太慢，请稍后重试'
      console.warn('Request timeout:', err.config?.url)
      MessageBox.alert(message, '错误', {
        confirmButtonText: '确定',
        type: 'error'
      })
    } else if (err.request) {
      // 请求已发送但没有收到响应
      message = '网络连接失败，请检查您的网络'
      console.warn('Network error:', err.message)
      MessageBox.alert(message, '错误', {
        confirmButtonText: '确定',
        type: 'error'
      })
    } else {
      // 其他错误
      console.error('Error:', err.message)
    }

    return Promise.reject(err || new Error(message))
  }
)

export function get(url, params) {
  return instance.get(url, { params })
}

export function post(url, data) {
  return instance.post(url, data)
}

export default instance

